import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledHome = styled("div")`
  width: 100%;
  box-sizing: border-box;
`;

const StyledRunningExperimentList = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  margin-bottom: 52px;
`;

const StyledRunningExperimentContainer = styled("div")`
  width: 100%;
  box-sizing: border-box;
`;

const StyledRunningExperimentTitle = styled(Typography)`
  margin-bottom: 16px;

  @media (min-width: 1024px) {
    margin-bottom: 24px;
  }
`;

const StyledRunningExperiment = styled("div")`
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  background-color: ${({ theme }) => theme.palette.common.white};
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  gap: 16px;
  min-height: 336px;

  & .running-experiment-tooltip {
    width: 100%;
  }

  & > .join-btn {
    margin: 0 auto;
    min-width: 157px;

    @media (min-width: 768px) {
      margin: 0 0 0 auto;
      max-width: 205px;
    }
  }

  &.loading {
    align-items: center;
    justify-content: center;
  }

  @media (min-width: 1024px) {
    border: 1px solid transparent;
    transition: all 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.palette.primary.main};
      box-shadow: 6px 12px 24px 0px #9ca8bf3d;
    }
  }

  @media (min-width: 1200px) {
    min-height: 106px;
    flex-direction: row;
    align-items: center;
    gap: 50px;
  }
`;

const StyledRunningExperimentInner = styled("div")`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1200px) {
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`;

const StyledRunningExperimentItem = styled("div")`
  width: max-content;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const StyledRunningExperimentDivider = styled("div")`
  width: 1.5px;
  height: 30px;
  background-color: #dee5f2;
  border-radius: 2px;
  display: none;
  visibility: hidden;

  @media (min-width: 1200px) {
    display: block;
    visibility: visible;
  }
`;

const StyledUpcomingExperiments = styled("div")`
  width: 100%;
  box-sizing: border-box;
`;

const StyledUpcomingExperimentsTabs = styled("div")`
  width: 100%;
  box-sizing: border-box;
  margin: 16px 0;
`;

const StyledUpcomingExperimentsList = styled("div")`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (min-width: 768px) {
    gap: 12px;
  }
`;

const StyledExperimentCard = styled("div")`
  width: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.palette.common.white};
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    width: calc(50% - 6px);
    padding: 24px;
  }

  @media (min-width: 1200px) {
    width: calc(25% - 12px);

    &:hover {
      border-color: ${({ theme }) => theme.palette.primary.main};
      transition: all 0.2s ease;
      box-shadow: 6px 12px 24px 0px #9ca8bf3d;
    }
  }

  @media (min-width: 1800px) {
    max-width: 270px;
  }
`;

const StyledExperimentCardHeader = styled("div")`
  width: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #d3dcea99;

  & .card-tooltip {
    max-width: 40%;
  }
`;

const StyledCardInfo = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  padding-top: 16px;
  gap: 16px;
`;

const StyledCardItem = styled("div")`
  width: max-content;
  max-width: calc(33.333% - 12px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  & .card-tooltip {
    max-width: 100%;
  }

  & .experiment-value {
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: 1024px) {
    max-width: 100%;
  }
`;

const StyledExperimentInfo = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  gap: 24px;

  @media (min-width: 768px) {
    padding: 0 16px;
  }

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const StyledExperimentInfoContent = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
`;

const StyledExperimentInfoIcon = styled("div")`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  padding: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  margin-bottom: 24px;
  background-color: ${({ theme }) => theme.palette.yellow.light};

  & > svg {
    width: 100%;
  }

  &.blue {
    background-color: ${({ theme }) => theme.palette.blue.light};
  }
`;

const StyledExperimentInfoFields = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 8px;
`;

const StyledExperimentInfoField = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.customGrey.soft};
  box-sizing: border-box;

  & .field-tooltip {
    max-width: 50%;
  }
`;

const StyledExperimentInfoFooter = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  gap: 14px;
`;

export {
  StyledHome,
  StyledRunningExperimentList,
  StyledRunningExperimentContainer,
  StyledRunningExperimentTitle,
  StyledRunningExperiment,
  StyledRunningExperimentInner,
  StyledRunningExperimentItem,
  StyledRunningExperimentDivider,
  StyledUpcomingExperiments,
  StyledUpcomingExperimentsTabs,
  StyledUpcomingExperimentsList,
  StyledExperimentCard,
  StyledExperimentCardHeader,
  StyledCardInfo,
  StyledCardItem,
  StyledExperimentInfo,
  StyledExperimentInfoContent,
  StyledExperimentInfoIcon,
  StyledExperimentInfoFooter,
  StyledExperimentInfoFields,
  StyledExperimentInfoField,
};
